if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleButtonClick = this.handleButtonClick.bind(this);
      this.handleCartError = this.handleCartError.bind(this);
      this.handleSubmitComplete = this.handleSubmitComplete.bind(this);
      this.handleGiftPackagingChange = this.handleGiftPackagingChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.mode = this.dataset.mode || 'after_scroll';
      this.modeDesktop = this.dataset.modeDesktop || 'disabled';
      this.desktopMediaQuery = window.matchMedia('(min-width: 768px)');
      this.isSubmitting = false;
      this.productForm = document.getElementById(this.dataset.productForm);
      this.productFormElement = this.productForm?.closest('product-form');
      this.submitButton = this.querySelector('[data-sticky-add]');
      this.sectionRoot = document.querySelector(`#MainProduct-${this.dataset.section}`);
      this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessageWrapper?.querySelector('.product-form__error-message');
      this.spinner = this.querySelector('.loading__spinner');
      this.giftMessage = this.querySelector('[data-sticky-gift-message]');
      this.giftPriceEl = this.querySelector('[data-sticky-gift-price]');
      this.footerElement = document.querySelector('footer');

      this.submitButton?.addEventListener('click', this.handleButtonClick);
      window.addEventListener('scroll', this.handleScroll);
      this.desktopMediaQuery.addEventListener('change', this.handleBreakpointChange);

      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange
      );
      this.cartErrorUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartError,
        this.handleCartError
      );

      this.productFormElement?.addEventListener('product-form:complete', this.handleSubmitComplete);
      this.sectionRoot?.addEventListener('gift-packaging:change', this.handleGiftPackagingChange);

      this.updateVisibility();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      this.desktopMediaQuery?.removeEventListener('change', this.handleBreakpointChange);
      this.submitButton?.removeEventListener('click', this.handleButtonClick);
      this.productFormElement?.removeEventListener('product-form:complete', this.handleSubmitComplete);
      this.sectionRoot?.removeEventListener('gift-packaging:change', this.handleGiftPackagingChange);

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      if (this.cartErrorUnsubscriber) {
        this.cartErrorUnsubscriber();
      }
    }

    getMainSubmitButton() {
      return this.productFormElement?.querySelector('[type="submit"]');
    }

    isBackInStockVisible() {
      const root = document.querySelector(`#MainProduct-${this.sectionId}`);
      const backInStock =
        document.getElementById(`BackInStockInline-${this.sectionId}`) ||
        root?.querySelector('[id^="BackInStockInline-"]');
      return Boolean(backInStock && !backInStock.classList.contains('hidden'));
    }

    getTargetElement() {
      const root = document.querySelector(`#MainProduct-${this.sectionId}`);
      if (!root) return null;

      return root.querySelector('custom-bundle') || root.querySelector('.product__buy-buttons');
    }

    getAbsoluteTop(element) {
      if (!element) return 0;
      let top = 0;
      while (element) {
        top += element.offsetTop || 0;
        element = element.offsetParent;
      }
      return top;
    }

    isFooterInView() {
      if (!this.footerElement) return false;
      const footerTopPosition = this.getAbsoluteTop(this.footerElement);
      return window.scrollY + window.innerHeight > footerTopPosition;
    }

    getActiveMode() {
      return this.desktopMediaQuery?.matches ? this.modeDesktop : this.mode;
    }

    updateVisibility() {
      const mode = this.getActiveMode();

      if (mode === 'disabled' || this.isBackInStockVisible() || this.isFooterInView()) {
        this.classList.remove('show');
        return;
      }

      if (mode === 'always') {
        this.classList.add('show');
        return;
      }

      const targetElement = this.getTargetElement();
      if (!targetElement) {
        this.classList.remove('show');
        return;
      }

      const isTargetElementOutOfView = this.getAbsoluteTop(targetElement) < window.scrollY;
      this.classList.toggle('show', isTargetElementOutOfView);
    }

    handleScroll() {
      this.updateVisibility();
    }

    handleBreakpointChange() {
      this.updateVisibility();
    }

    handleButtonClick() {
      if (!this.productForm || !this.submitButton || this.isSubmitting) return;
      if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      const mainSubmitButton = this.getMainSubmitButton();
      if (!mainSubmitButton) return;

      if (mainSubmitButton.disabled) {
        this.submitButton.setAttribute('disabled', 'disabled');
        return;
      }

      if (mainSubmitButton.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.handleErrorMessage();

      try {
        if (typeof this.productForm.requestSubmit === 'function') {
          this.productForm.requestSubmit();
        } else {
          this.productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      } catch (error) {
        this.setLoading(false);
        return;
      }

      if (!mainSubmitButton.classList.contains('loading')) {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.submitButton?.classList.toggle('loading', isLoading);

      if (isLoading) {
        this.submitButton?.setAttribute('aria-disabled', 'true');
      } else {
        this.submitButton?.removeAttribute('aria-disabled');
      }

      this.spinner?.classList.toggle('hidden', !isLoading);
    }

    handleSubmitComplete() {
      if (!this.isSubmitting) return;
      this.setLoading(false);
    }

    handleCartError(event) {
      if (!this.isSubmitting) return;
      if (event?.source && event.source !== 'product-form') return;

      const message = typeof event?.errors === 'string' ? event.errors : event?.message;
      if (typeof message === 'string' && message) {
        this.handleErrorMessage(message);
      }

      this.setLoading(false);
    }

    handleErrorMessage(errorMessage = false) {
      if (!this.errorMessageWrapper) return;

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    handleVariantChange({ data: { sectionId, html } }) {
      const mainSectionId = this.sectionId.replace(/^template--\d+__/, '');
      const eventSectionId = sectionId.replace(/^template--\d+__/, '');

      if (mainSectionId !== eventSectionId) return;

      this.updateStickyOptionsFromHTML(html);
      this.syncDisabledFromMain();
      this.updateVisibility();
    }

    handleGiftPackagingChange(event) {
      const giftPriceCents = Number(event.detail?.price) || 0;
      this.toggleGiftMessage(giftPriceCents > 0, giftPriceCents);
    }

    toggleGiftMessage(isVisible, giftPriceCents = 0) {
      this.giftMessage?.toggleAttribute('hidden', !isVisible);

      if (this.giftPriceEl) {
        this.giftPriceEl.textContent = isVisible ? `+${this.formatMoney(giftPriceCents)}` : '';
      }
    }

    formatMoney(cents) {
      if (typeof formatMoney === 'function') {
        return formatMoney(
          cents,
          window.theme?.settings?.money_with_currency_format || '{{amount}}'
        );
      }

      return String(cents);
    }

    syncDisabledFromMain() {
      const mainSubmitButton = this.getMainSubmitButton();
      if (!mainSubmitButton || !this.submitButton || this.isSubmitting) return;

      if (mainSubmitButton.disabled) {
        this.submitButton.setAttribute('disabled', 'disabled');
      } else {
        this.submitButton.removeAttribute('disabled');
      }
    }

    updateStickyOptionsFromHTML(html) {
      const sourceOptionsContainer = html.querySelector('.selected-variant-details .options-container');
      const destinationOptionsContainer = this.querySelector('.selected-variant-details .options-container');

      if (!sourceOptionsContainer || !destinationOptionsContainer) return;

      const sourceOptions = sourceOptionsContainer.querySelectorAll('.selected-variant-option');
      const destinationOptions = destinationOptionsContainer.querySelectorAll('.selected-variant-option');

      sourceOptions.forEach((sourceOption, index) => {
        if (destinationOptions[index]) {
          destinationOptions[index].textContent = sourceOption.textContent;
        }
      });
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}
